"use client";

import * as React from "react";
import { add, differenceInCalendarDays, format } from "date-fns";
import { TZDate } from "@date-fns/tz";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./button";
import { useValueChanged } from "../../hooks/useValueChanged";
import { Calendar as CalendarComponent } from "./calendar";
import type { DateRange, Matcher } from "react-day-picker";

type ViewMode = "calendar" | "month" | "year";

const months = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

/**
 * Keep `current`'s time-of-day while moving onto `newDay`'s calendar day.
 * Uses calendar-day distance (not ms/86400000) so a 25-hour DST fall-back day does not skip ahead.
 */
export function withPreservedTimeOnDay(current: Date, newDay: Date): Date {
	return add(current, { days: differenceInCalendarDays(newDay, current) });
}

/** Calendar-month start in `timeZone` when set; otherwise browser-local. */
function monthStart(year: number, month: number, timeZone?: string): Date {
	return timeZone
		? new TZDate(year, month, 1, timeZone)
		: new Date(year, month, 1);
}

/**
 * Tag an absolute instant with `timeZone` so getters/format match that zone's wall clock. Pass a
 * zoned `date` to `DatePickerCalendar` with a `timeZone`, so a pick moves it by days in that zone.
 */
export function toZoned(date: Date, timeZone?: string): Date {
	return timeZone ? new TZDate(date.getTime(), timeZone) : new Date(date);
}

/**
 * Re-tag every Date boundary in a `disabledDays` matcher into `timeZone`, so react-day-picker (and
 * our own range helpers) evaluate it against the same zoned day cells as the calendar instead of
 * against browser-local day boundaries. Non-Date matchers (booleans, day-of-week, functions) are
 * returned unchanged; `timeZone` undefined is the identity. Mirrors how `minDate`/`maxDate`/
 * `disabledDates` are already zoned by the pickers.
 */
export function toZonedMatchers(
	disabledDays: Matcher | Matcher[] | undefined,
	timeZone?: string,
): Matcher | Matcher[] | undefined {
	if (!disabledDays || !timeZone) {
		return disabledDays;
	}
	return Array.isArray(disabledDays)
		? disabledDays.map((matcher) => zoneMatcher(matcher, timeZone))
		: zoneMatcher(disabledDays, timeZone);
}

function zoneMatcher(matcher: Matcher, timeZone: string): Matcher {
	if (matcher instanceof Date) {
		return new TZDate(matcher.getTime(), timeZone);
	}

	if (Array.isArray(matcher)) {
		return matcher.map((d) => new TZDate(d.getTime(), timeZone));
	}

	if (typeof matcher === "object" && matcher !== null) {
		if ("from" in matcher) {
			const { from, to } = matcher as DateRange;
			return {
				from: from ? new TZDate(from.getTime(), timeZone) : from,
				to: to ? new TZDate(to.getTime(), timeZone) : to,
			};
		}

		if ("before" in matcher || "after" in matcher) {
			const { before, after } = matcher as { before?: Date; after?: Date };
			const zoned: { before?: Date; after?: Date } = {};
			if (before) {
				zoned.before = new TZDate(before.getTime(), timeZone);
			}
			if (after) {
				zoned.after = new TZDate(after.getTime(), timeZone);
			}
			return zoned as Matcher;
		}
	}

	return matcher;
}

type DatePickerCalendarProps = {
	mode?: "single" | "range";
	date?: Date;
	onDateChange?: (date: Date | undefined) => void;
	dateRange?: DateRange;
	onDateRangeChange?: (dateRange: DateRange | undefined) => void;
	maxDate?: Date;
	minDate?: Date;
	disabledDates?: Date[];
	disabledDays?: Matcher | Matcher[];
	/** IANA timezone the calendar renders/selects days in (react-day-picker native). */
	timeZone?: string;
	onViewModeChange?: (viewMode: ViewMode) => void;
	onDayClick?: (date: Date) => void;
} & React.ComponentProps<typeof CalendarComponent>;

type CalendarViewProps = {
	mode?: "single" | "range";
	date?: Date | undefined;
	onDateSelect?: (date: Date | undefined) => void;
	dateRange?: DateRange;
	onDateRangeSelect?: (dateRange: DateRange | undefined) => void;
	displayDate: Date;
	onMonthChange: (month: Date) => void;
	maxDate?: Date;
	minDate?: Date;
	disabledDates?: Date[];
	disabledDays?: Matcher | Matcher[];
	timeZone?: string;
	onDayClick?: (date: Date) => void;
	className?: string;
};

type MonthViewProps = {
	displayDate: Date;
	onMonthSelect: (monthIndex: number) => void;
	maxDate?: Date;
	minDate?: Date;
	disabledDates?: Date[];
};

type YearViewProps = {
	displayDate: Date;
	yearRange: { start: number; end: number };
	onYearSelect: (year: number) => void;
	maxDate?: Date;
	minDate?: Date;
};

type NavigationHeaderProps = {
	viewMode: ViewMode;
	displayDate: Date;
	yearRange: { start: number; end: number };
	onViewModeChange: (mode: ViewMode) => void;
	onNavigate: (direction: "prev" | "next") => void;
};

function CalendarView({
	date,
	dateRange,
	displayDate,
	onDateSelect,
	onDateRangeSelect,
	onMonthChange,
	maxDate,
	minDate,
	disabledDates,
	mode = "single",
	onDayClick,
	disabledDays,
	timeZone,
}: CalendarViewProps) {
	const handleSelect = (newDay: Date | undefined) => {
		if (!newDay) {
			return;
		}
		if (mode === "single") {
			if (!date) {
				onDateSelect?.(newDay);
				return;
			}
			onDateSelect?.(withPreservedTimeOnDay(date, newDay));
		}
	};

	const handleDayClick = (clickedDate: Date) => {
		if (onDayClick) {
			onDayClick(clickedDate);
		} else if (mode === "single") {
			handleSelect(clickedDate);
		}
	};

	const handleRangeSelect = (range: DateRange | undefined) => {
		if (!onDayClick) {
			onDateRangeSelect?.(range);
		}
	};

	const commonProps = {
		month: displayDate,
		onMonthChange,
		onDayClick: handleDayClick,
		timeZone,
		showOutsideDays: true,
		fixedWeeks: false,
		components: {
			CaptionLabel: () => <>{null}</>,
			Nav: () => <>{null}</>,
			MonthCaption: () => <>{null}</>,
			PreviousMonthButton: () => <>{null}</>,
			NextMonthButton: () => <>{null}</>,
		},
		modifiers: {
			// eslint-disable-next-line react-hooks/purity -- calendar "today" must reflect the current instant in the fund timezone
			today: timeZone ? new TZDate(Date.now(), timeZone) : new Date(),
		},
		modifiersClassNames: {
			today: "text-[#FF0000]",
		},
		disabled: [
			...(minDate ? [{ before: minDate }] : []),
			...(maxDate ? [{ after: maxDate }] : []),
			...(disabledDates ? disabledDates : []),
			...(disabledDays
				? Array.isArray(disabledDays)
					? disabledDays
					: [disabledDays]
				: []),
		],
		excludeDisabled: true,
	};

	return (
		<div data-testid="date-picker-calendar-view" className="px-1">
			{mode === "single" ? (
				<CalendarComponent
					{...commonProps}
					mode="single"
					selected={date}
					onSelect={handleSelect}
				/>
			) : (
				<CalendarComponent
					{...commonProps}
					mode="range"
					selected={dateRange}
					onSelect={handleRangeSelect}
				/>
			)}
		</div>
	);
}

function MonthView({
	displayDate,
	onMonthSelect,
	maxDate,
	minDate,
}: MonthViewProps) {
	const currentMonth = displayDate.getMonth();
	const currentYear = displayDate.getFullYear();

	const monthsToShow = months.map((month, index) => {
		let disabled = false;

		if (minDate && currentYear < minDate.getFullYear()) {
			disabled = true;
		} else if (maxDate && currentYear > maxDate.getFullYear()) {
			disabled = true;
		} else if (
			minDate &&
			currentYear === minDate.getFullYear() &&
			index < minDate.getMonth()
		) {
			disabled = true;
		} else if (
			maxDate &&
			currentYear === maxDate.getFullYear() &&
			index > maxDate.getMonth()
		) {
			disabled = true;
		}

		return { month, index, disabled };
	});

	return (
		<div
			data-testid="date-picker-month-view"
			className="mt-2 grid grid-cols-3 gap-2 p-2 min-w-[248px] h-[250px] place-items-stretch"
		>
			{monthsToShow.map(({ month, index }) => (
				<Button
					key={month}
					variant={index === currentMonth ? "default" : "ghost"}
					size="sm"
					className="h-12 text-sm"
					onClick={() => onMonthSelect(index)}
					disabled={monthsToShow[index].disabled}
				>
					{month}
				</Button>
			))}
		</div>
	);
}

function YearView({
	displayDate,
	yearRange,
	onYearSelect,
	maxDate,
	minDate,
}: YearViewProps) {
	const currentYear = displayDate.getFullYear();
	const years = Array.from({ length: 12 }, (_, i) => yearRange.start + i);
	const yearsToShow = years.map((year) => {
		if (minDate && year < minDate.getFullYear()) {
			return { year, disabled: true };
		}
		if (maxDate && year > maxDate.getFullYear()) {
			return { year, disabled: true };
		}
		return { year, disabled: false };
	});

	return (
		<div
			data-testid="date-picker-year-view"
			className="mt-2 grid grid-cols-4 gap-1 p-2 min-w-[248px] h-[250px] w-full"
		>
			{yearsToShow.map(({ year, disabled }) => (
				<Button
					key={year}
					variant={year === currentYear ? "default" : "ghost"}
					size="sm"
					className="h-12 text-sm"
					onClick={() => onYearSelect(year)}
					disabled={disabled}
				>
					{year}
				</Button>
			))}
		</div>
	);
}

function NavigationHeader({
	viewMode,
	displayDate,
	yearRange,
	onViewModeChange,
	onNavigate,
}: NavigationHeaderProps) {
	const renderTitle = () => {
		switch (viewMode) {
			case "year":
				return (
					<Button
						variant="ghost"
						size="sm"
						className="text-sm font-medium"
						onClick={() => onViewModeChange("calendar")}
					>
						{yearRange.start} – {yearRange.end}
					</Button>
				);
			case "month":
				return (
					<Button
						variant="ghost"
						size="sm"
						className="text-sm font-medium"
						onClick={() => onViewModeChange("year")}
					>
						{displayDate.getFullYear()}
					</Button>
				);
			case "calendar":
				return (
					<div className="flex items-center">
						<Button
							variant="ghost"
							size="sm"
							className="text-sm font-medium"
							onClick={() => onViewModeChange("month")}
						>
							{format(displayDate, "MMMM")}
						</Button>
						<Button
							variant="ghost"
							size="sm"
							className="text-sm font-medium"
							onClick={() => onViewModeChange("year")}
						>
							{displayDate.getFullYear()}
						</Button>
					</div>
				);
		}
	};

	return (
		<div
			data-testid="date-picker-navigation-header"
			className="flex items-center justify-between mx-4 mt-1"
		>
			<Button
				variant="ghost"
				size="sm"
				className="h-8 w-8 p-0"
				onClick={() => onNavigate("prev")}
			>
				<ChevronLeft className="h-4 w-4" />
			</Button>
			<div>{renderTitle()}</div>
			<Button
				variant="ghost"
				size="sm"
				className="h-8 w-8 p-0"
				onClick={() => onNavigate("next")}
			>
				<ChevronRight className="h-4 w-4" />
			</Button>
		</div>
	);
}

export function DatePickerCalendar({
	mode = "single",
	date,
	onDateChange,
	dateRange,
	onDateRangeChange,
	minDate,
	maxDate,
	disabledDates,
	disabledDays,
	timeZone,
	onViewModeChange,
	onDayClick,
	defaultMonth,
}: DatePickerCalendarProps) {
	const [displayDate, setDisplayDate] = React.useState(() =>
		toZoned(date || dateRange?.from || defaultMonth || new Date(), timeZone),
	);

	const [viewMode, setViewMode] = React.useState<ViewMode>("calendar");
	const [yearRange, setYearRange] = React.useState(() => {
		const currentYear = toZoned(
			date || dateRange?.from || new Date(),
			timeZone,
		).getFullYear();
		return { start: currentYear - 8, end: currentYear + 3 };
	});

	const externalAnchorKey =
		mode === "single"
			? `${date?.getTime() ?? "none"}\0${timeZone ?? ""}`
			: `${dateRange?.from?.getTime() ?? "none"}\0${dateRange?.to?.getTime() ?? "none"}\0${timeZone ?? ""}`;
	const externalAnchorChanged = useValueChanged(externalAnchorKey);
	if (externalAnchorChanged && mode === "single" && date) {
		setDisplayDate(toZoned(date, timeZone));
	} else if (externalAnchorChanged && mode === "range" && dateRange?.from) {
		setDisplayDate(toZoned(dateRange.from, timeZone));
	}

	React.useEffect(() => {
		onViewModeChange?.(viewMode);
	}, [viewMode, onViewModeChange]);

	const handleMonthSelect = (monthIndex: number) => {
		setDisplayDate(monthStart(displayDate.getFullYear(), monthIndex, timeZone));
		setViewMode("calendar");
	};

	const handleYearSelect = (year: number) => {
		setDisplayDate(monthStart(year, displayDate.getMonth(), timeZone));
		setViewMode("calendar");
	};

	const handleNavigate = (direction: "prev" | "next") => {
		if (viewMode === "year") {
			const offset = direction === "prev" ? -12 : 12;
			setYearRange((prev) => ({
				start: prev.start + offset,
				end: prev.end + offset,
			}));
		} else if (viewMode === "month") {
			setDisplayDate(
				monthStart(
					displayDate.getFullYear() + (direction === "prev" ? -1 : 1),
					displayDate.getMonth(),
					timeZone,
				),
			);
		} else {
			setDisplayDate(
				monthStart(
					displayDate.getFullYear(),
					displayDate.getMonth() + (direction === "prev" ? -1 : 1),
					timeZone,
				),
			);
		}
	};

	const renderCurrentView = () => {
		switch (viewMode) {
			case "year":
				return (
					<YearView
						displayDate={displayDate}
						yearRange={yearRange}
						onYearSelect={handleYearSelect}
						maxDate={maxDate}
						minDate={minDate}
					/>
				);
			case "month":
				return (
					<MonthView
						displayDate={displayDate}
						onMonthSelect={handleMonthSelect}
						maxDate={maxDate}
						minDate={minDate}
					/>
				);
			case "calendar":
				return (
					<CalendarView
						date={date}
						dateRange={dateRange}
						displayDate={displayDate}
						onDateSelect={onDateChange}
						onDateRangeSelect={onDateRangeChange}
						onMonthChange={setDisplayDate}
						maxDate={maxDate}
						minDate={minDate}
						disabledDates={disabledDates}
						disabledDays={disabledDays}
						timeZone={timeZone}
						mode={mode}
						onDayClick={onDayClick}
					/>
				);
		}
	};

	return (
		<div data-testid="date-picker-calendar" className="mt-2">
			<NavigationHeader
				viewMode={viewMode}
				displayDate={displayDate}
				yearRange={yearRange}
				onViewModeChange={setViewMode}
				onNavigate={handleNavigate}
			/>
			{renderCurrentView()}
		</div>
	);
}
